import { lookupAuthProfile } from '@/api/authProfileApi';
import logger from '@/util/logger';
import { DeviceDao, HomeDao, UserDao, UserLimitsDao, UserRoleDao, atomWithLoadMore } from '@rock/db-admin';
import { type AuthProfile, type User, type UserId, UserIdSchema } from '@rock/types';
import { shallowEqual } from '@rock/util';
import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { currentUserIdState } from './auth.state';

const log = logger('user.state');

const dao = new UserDao();
const userRoleDao = new UserRoleDao();
const homeDao = new HomeDao();
const userLimitsDao = new UserLimitsDao();
const deviceDao = new DeviceDao();

export const userState = atomFamily((userId: UserId | undefined) => dao.userAtom(userId), shallowEqual);

type UsersFilter = { emailPrefix: string; includeTest: boolean };

/**
 * Paginated list of users — most recently active first, or email order while filtering.
 * `includeTest` is applied in the query rather than to the loaded page, so a page of N is N real
 * users instead of however many survive a client-side filter.
 */
export const usersState = atomWithLoadMore<User, UsersFilter>(
    ({ limitAtom, filterAtom, sortAtom }) => {
        const emailPrefixAtom = atom((get) => get(filterAtom).emailPrefix);
        const includeTestAtom = atom((get) => get(filterAtom).includeTest);
        return dao.usersAtom(limitAtom, emailPrefixAtom, includeTestAtom, sortAtom);
    },
    { initialFilter: { emailPrefix: '', includeTest: false } },
);

/**
 * The most recently active real users — the Dashboard's Recent Users card. Test users are excluded
 * by the query itself, so the 8 fetched are the 8 shown.
 */
export const recentUsersState = dao.recentlyActiveUsersAtom(8);

/** Total signed-up real users (server count aggregation, one-shot) — the Dashboard's Users hero. */
export const usersCountState = atom(async () => dao.countUsers());

/** Every home a user is a member of (active and inactive) — the user detail page's Homes tab. */
export const userHomesState = atomFamily(
    (userId: UserId | undefined) => homeDao.homesForMemberAtom(userId),
    shallowEqual,
);

/** A user's server-authoritative daily AI usage counters — undefined until they first hit a cap-metered action. */
export const userLimitsState = atomFamily(
    (userId: UserId | undefined) => userLimitsDao.userLimitsAtom(userId),
    shallowEqual,
);

/** A user's registered devices, most recently active first — the user detail page's Devices tab. */
export const userDevicesState = atomFamily((userId: UserId | undefined) => deviceDao.devicesAtom(userId), shallowEqual);

/** The (small) set of role-bearing users, for cross-referencing admin status in the users table. */
export const userRolesState = userRoleDao.userRolesAtom();

/**
 * The signed-in user's own `user-roles` doc (rules allow self-read; `{}` when the doc is absent).
 * `RequireRole` gates the whole admin UI on this so a role-less teammate sees "Access Needed"
 * instead of permission-denied failures on every page.
 */
export const myRolesState = atom(async (get) => {
    const userId = get(currentUserIdState);
    if (!userId) {
        return undefined;
    }
    return await userRoleDao.getUserRoles(UserIdSchema.parse(userId));
});

/**
 * A Firebase Auth account's name, email and photo — the fallback identity for uids with no `users/{id}`
 * doc (admins are auth-only). Resolves to `undefined` if the lookup fails, so callers show the raw id.
 */
export const authProfileState = atomFamily((userId: UserId) =>
    atom(async (): Promise<AuthProfile | undefined> => {
        try {
            return (await lookupAuthProfile({ userId })) ?? undefined;
        } catch (err) {
            log.error('Failed to look up auth profile', { err, userId });
            return undefined;
        }
    }),
);
